type Category = 'marketingAndAnalytics' | 'functional' | 'advertising' | 'custom';
export const CONSENT_COOKIE = 'tracking-preferences';

const readCookieRaw = (name: string = CONSENT_COOKIE): string | null => {
  const m = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));

  return m ? decodeURIComponent(m[1]) : null;
};

const normalizePrefs = (val: unknown): Record<string, boolean> | null => {
  if (val == null) {
    return null;
  }

  if (typeof val === 'string') {
    if (val === 'true' || val === 'false') {
      return { marketingAndAnalytics: val === 'true' };
    }
    try {
      return normalizePrefs(JSON.parse(val));
    } catch {
      return null;
    }
  }

  if (typeof val === 'boolean') {
    return { marketingAndAnalytics: val };
  }

  if (typeof val === 'object') {
    const obj = val as Record<string, unknown>;
    // @segment/consent-manager writes the per-category answers NESTED under
    // `custom`, not at the top level:
    //   {"version":1,"destinations":{...},"custom":{"marketingAndAnalytics":true,...}}
    // Reading only the top level made every category read as false even right
    // after the visitor accepted them. Check both so the Segment shape and the
    // older flat shapes all resolve correctly.
    const customRaw = obj['custom'];
    const custom = (customRaw && typeof customRaw === 'object' ? customRaw : {}) as Record<string, unknown>;
    const pick = (...keys: string[]): boolean => keys.some(k => Boolean(obj[k]) || Boolean(custom[k]));

    // `custom` is deliberately NOT reported as a category of its own. It used to
    // be `Boolean(obj['custom'])`, which is truthy merely because the object
    // exists -- so a visitor who refused every category still came back as
    // consenting under hasConsent('any').
    const out: Record<string, boolean> = {
      marketingAndAnalytics: pick('marketingAndAnalytics', 'marketing', 'analytics', 'performance'),
      functional: pick('functional'),
      advertising: pick('advertising'),
    };

    return out;
  }

  return null;
};

export const parseConsentCookie = (raw = readCookieRaw()): Record<string, boolean> | null => normalizePrefs(raw);

export const hasConsent = (category: Category | 'any' = 'marketingAndAnalytics'): boolean => {
  const prefs = parseConsentCookie();
  if (!prefs) {
    return false;
  }
  if (category === 'any') {
    return Object.values(prefs).some(Boolean);
  }

  return Boolean(prefs[category] ?? prefs['marketingAndAnalytics']);
};

// `category` defaults to marketingAndAnalytics rather than 'any' on purpose.
// The only caller is AttributionManager, which uses this to promote UTM and
// gclid values out of sessionStorage into a 90-day cookie. That is marketing
// data, so accepting only the functional category must not trigger it.
export const onConsentReady = (
  cb: () => void,
  { intervalMs = 500, timeoutMs = 30_000, category = 'marketingAndAnalytics' as Category } = {},
): (() => void) => {
  let last = readCookieRaw();
  if (hasConsent(category)) {
    cb();

    return () => {};
  }
  const start = Date.now();
  const id = window.setInterval(() => {
    const now = readCookieRaw();
    const changed = now !== last;
    const timedOut = Date.now() - start > timeoutMs;
    if ((changed && hasConsent(category)) || timedOut) {
      clearInterval(id);
      if (!timedOut) {
        cb();
      }
    }
    last = now;
  }, intervalMs);

  return () => clearInterval(id);
};
